import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { completeExternalAuth } from '@/api/externalAuth.api';
import type { ExternalPendingResult } from '@/types/externalAuth';
import { Button } from '@/components/UI/Button';
import { signIn } from '@/redux/auth/auth.slice';
import { useAppDispatch } from '@/redux/store.hook';
import { getAuthRefreshDeadline } from '@/utils/authToken';
import { isPblUser } from '@/utils/pblRole';
import { resolvePblLoginRedirect } from '../SignIn/pblLoginRedirect';
import { v2UiEnabled } from '@/components/DmpV2/features';
import { resolveDmpPurchaseReturnPath } from '@/components/DmpV2/purchaseReturnPath';
import { safeInternalReturnPath } from '@/utils/safeReturnPath';
import AuthContainer from '@/components/Container/AuthContainer';
import { ActionRow, Intro, StatusMessage } from '../externalStyles';

const ExternalAuthCallbackPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { provider: providerParam } = useParams();
  // @ts-ignore -- the repository's fully inferred i18n catalogue exceeds TypeScript's instantiation depth.
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [pending, setPending] = useState<ExternalPendingResult | null>(null);
  const callbackStarted = useRef(false);
  const provider = providerParam || 'wechat';

  useEffect(() => {
    if (callbackStarted.current) return;
    callbackStarted.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const callbackPayload: Record<string, string> = {};
    params.forEach((value, key) => {
      callbackPayload[key] = value;
    });
    window.history.replaceState({}, document.title, window.location.pathname);

    if (!state || !code) {
      setError(t('pages.auth.external.callbackMissing'));
      return;
    }

    completeExternalAuth(provider, callbackPayload)
      .then(async ({ data: response }) => {
        const result = response.data;
        if ('status' in result) {
          const normalized: ExternalPendingResult = result.status === 'binding_required'
            ? {
              status: 'registration_or_binding_required',
              intent_id: result.binding_intent_id,
              expires_at: result.expires_at,
              return_path: result.return_path,
              provider,
            }
            : result;

          if (normalized.status === 'registration_or_binding_required' && normalized.intent_id) {
            window.sessionStorage.setItem('external.bindingIntentId', normalized.intent_id);
            window.sessionStorage.setItem('external.pendingBindingIntentId', normalized.intent_id);
            setPending(normalized);
            return;
          }

          setPending(normalized);
          return;
        }

        if (!('access_token' in result)) throw new Error(response.message || t('pages.auth.external.callbackFailedTitle'));
        await dispatch(signIn({
          isLogin: true,
          accessToken: result.access_token,
          userData: result.user,
          applications: result.applications,
          registDone: result.user.stage >= 4,
          refreshTokenTime: getAuthRefreshDeadline(result.expires_in),
        }));

        const returnPath = safeInternalReturnPath(result.return_path);
        navigate(returnPath || (isPblUser(result.user)
          ? resolvePblLoginRedirect(result.user, result.applications)
          : result.user.stage < 4 ? `/registration?step=${result.user.stage + 1}` : (v2UiEnabled() ? resolveDmpPurchaseReturnPath(null, String(result.user.id)) : null) || '/home'), { replace: true });
      })
      .catch((reason) => setError(
        reason?.response?.data?.data?.code
        || reason?.response?.data?.message
        || reason?.message
        || t('pages.auth.external.callbackFailedTitle')
      ));
  }, [dispatch, navigate, provider, t]);

  const continueWithExistingAccount = () => {
    const returnPath = safeInternalReturnPath(pending?.return_path);
    navigate(`/auth/signin?external_binding=1${returnPath ? `&path=${encodeURIComponent(returnPath)}` : ''}`, { replace: true });
  };

  const createAccount = () => {
    if (!pending?.intent_id) return;
    navigate(`/registration?step=1&external_intent=${encodeURIComponent(pending.intent_id)}`, { replace: true });
  };

  const statusText = error || (pending?.status === 'registration_or_binding_required'
    ? t('pages.auth.external.chooseAccountPath')
    : pending?.status === 'recovery_pending_admin_approval'
      ? t('pages.auth.external.recoveryPending')
      : t('pages.auth.external.callbackPending'));

  return <AuthContainer title={error ? t('pages.auth.external.callbackFailedTitle') : t('pages.auth.external.callbackTitle')}>
    {!error && pending?.status === 'registration_or_binding_required' && <Intro>{t('pages.auth.external.callbackChoiceHelp')}</Intro>}
    <StatusMessage tone={error ? 'error' : pending?.status === 'recovery_pending_admin_approval' ? 'success' : 'info'}
      role={error ? 'alert' : 'status'} aria-live="polite">
      {statusText}
    </StatusMessage>
      {pending?.status === 'registration_or_binding_required' && <ActionRow>
        <Button type="button" size="lg" variant="primary" onClick={continueWithExistingAccount}>
          {t('pages.auth.external.linkExisting')}
        </Button>
        {process.env.REACT_APP_EXTERNAL_REGISTRATION_ENABLED === 'true' && <Button type="button" size="lg" variant="primary-invert" onClick={createAccount}>
          {t('pages.auth.external.createAccount')}
        </Button>}
      </ActionRow>}
      {error && <ActionRow><Button type="button" size="lg" variant="primary" rounded={false}
        onClick={() => navigate('/auth/signin', { replace: true })}>
        {t('pages.auth.external.backToSignIn')}
      </Button></ActionRow>}
  </AuthContainer>;
};

export default ExternalAuthCallbackPage;
